<i18n>
{
  "ja": {
    "warehouses-found": "件の倉庫が見つかりました",
    "warehouses-at": "{location}の倉庫一覧",
    "warehouses-with": "{location} {condition}の倉庫一覧",
    "search-condition-at": "{location}のこだわり条件",
    "search-condition-area": "{location}のエリア",
    "change-search-filter": "検索条件を変更する",
    "no-warehouse-found": "倉庫が見つかりませんでした。",
    "try-changing-saerch-criteria": "条件を変更して検索してみましょう！",
    "search-from-map": "地図から探す",
    "search-warehouses": "エリア一覧",
    "area-text": "{area}エリア"
  }
}
</i18n>

<template>
<div v-if="!isError" class="page-area-search">
  <WarehouseMain :location="params.prefecture" />
  <breadcrumbs-list :list="breadcrumbs" class="search-bread-crumbs lg:bg-gray-200 lg:justify-start px-6 md:px-10 xl:px-32" />
  <div v-if="isLoading" class="text-center">
    <p>Loading...</p>
  </div>
  <div v-else class="mx-6 md:mx-10 md:my-10 xl:mx-32 xl:my-20 relative">
    <section class="md:flex text-center lg:text-left mt-6">
      <section v-if="warehouses" class="search-results flex-grow mb-5">
        <h1 v-if="isTagSearch" class="text-2xl font-bold">{{ $t('warehouses-with', { condition: $t(`search.${searchTag}`), location: params.prefecture }) }}</h1>
        <h1 v-else-if="params.area" class="text-2xl font-bold">{{ $t('warehouses-at', { location: areaName }) }}</h1>
        <h1 v-else class="text-2xl font-bold">{{ $t('warehouses-at', {location: params.prefecture }) }}</h1>
        <div v-if="warehouseCount === 0" class="no-result w-full mb-20 lg:mt-20 py-6 flex flex-col lg:flex-row lg:bg-gray-200 lg:py-10">
          <div class="order-2 lg:order-1 flex items-center justify-center">
            <div class="image-wrapper rounded-full p-10 bg-gray-200 lg:bg-transparent w-56 h-56 flex items-center justify-center content-center">
              <img class="disappointed-shipper h-40 lg:h-auto" src="@/assets/img/disappointed_shipper.svg" alt="No Search Results">
            </div>
          </div>
          <div class="font-bold order-1 lg:order-2 mb-5 lg:flex lg:content-center lg:items-center">
            <div class="lg:text-lg">
              <span class="lg:border-b-4 lg:border-secondary lg:leading-loose">{{ $t('no-warehouse-found') }}</span> <br/>
              <span class="lg:border-b-4 lg:border-secondary lg:leading-loose">{{ $t('try-changing-saerch-criteria') }}</span>
            </div>
          </div>
        </div>
        <div v-else class="has-results flex justify-center md:justify-between">
          <div class="warehouse-counter-container mt-6 md:mt-8 md:mb-3">
            <span class="warehouse-count text-2xl font-bold text-secondary">{{ warehouseCount }}</span>
            <span class="text-sm font-bold">{{ $t('warehouses-found') }}</span>
          </div>
          <div class="hidden lg:block self-end mb-3 text-secondary border border-secondary p-2 rounded">
            <a :href="`/warehouses/search/map/${params.prefecture}`">
              <img class="inline text-secondary" src="~/assets/img/warehouse/icon_map.svg" />
              <span class="font-bold">{{ $t('search-from-map') }}</span></a>
          </div>
        </div>
        <ul class="flex flex-col">
          <li v-for="(wh, index) in warehouses" :key="index" class="flex-grow border-b" :class="{ 'lg:border-t': index == 0 }">
            <warehouse-item :warehouse="wh" :area="params.prefecture" />
          </li>
        </ul>
        <client-only>
          <pagination
            v-if="warehouseCount > 0"
            :current-page="currentPage"
            :total-count="warehouseCount"
            :last-page="lastPage"
            :per-page="searchConditions.perPage"
            @page-change="onPageChange"
            @page-size-change="onPageSizeChange"
          />
        </client-only>
      </section>
      <client-only>
        <section class="search-filter-trigger fixed bottom-0 right-0 left-0 bg-accent lg:hidden flex justify-center p-4">
          <button class="bg-white rounded flex justify-center items-center py-1 px-4" @click="showSearchFilter">
            <span class="font-bold text-sm">{{ $t('change-search-filter') }}</span>
            <span class="material-icons">search</span>
          </button>
        </section>
      </client-only>
      <client-only>
        <section class="search-filter self-start bg-gray-200 lg:ml-10 lg:block lg:relative lg:mt-0" :class="searchFilterDisplayed">
          <search-filter
            :query="searchConditions"
            :areas="publicAreas"
            :total-count="warehouseCount"
            @search="searchByFilter"
            @close="closeSearchFilter"
          />
        </section>
      </client-only>
    </section>
    <section v-if="areaLink && areaLink.length > 0" class="search-tags my-20">
      <div class="text-lg font-bold mb-4">{{ $t('search-condition-area', { location: params.prefecture }) }}</div>
      <div class="text-secondary bg-gray-200 p-4 flex flex-wrap">
        <nuxt-link
          v-for="({url, name}, index) in areaLink"
          :key="index"
          :to="url"
          class="mr-4 whitespace-no-wrap leading-loose md:leading-relaxed cursor-pointer"
        >
          {{ name }}
        </nuxt-link>
      </div>
    </section>
    <section v-if="tags && warehouseCount > 0" class="search-tags my-20">
      <div class="text-lg font-bold mb-4">{{ $t('search-condition-at', { location: params.prefecture }) }}</div>
      <div class="text-secondary bg-gray-200 p-4 flex flex-wrap">
        <span
          v-for="(tag, index) in filterPublicTags(tags)"
          :key="index"
          class="tag mr-4 whitespace-no-wrap leading-loose md:leading-relaxed cursor-pointer"
          @click="searchByTag(tag)"
        >
          {{ $t(`search.${tag}`)}}
        </span>
      </div>
    </section>
    <div class="block lg:hidden my-8 text-center mb-20">
      <a class="bg-secondary text-white border border-secondary py-4 px-16 rounded-full" :href="`/warehouses/search/map/${params.prefecture}`">
        <img class="inline text-secondary" src="~/assets/img/warehouse/icon_map_white.svg" />
        <span>{{ $t('search-from-map') }}</span></a>
    </div>
  </div>
</div>
</template>

<style lang="sass" scoped>
@import '~assets/css/warehouse-search.scss'

.search-bread-crumbs
  @apply  pt-2 pb-1
.page-area-search > section
  max-width: 1080px
  margin-left: auto
  margin-right: auto
.search-filter-trigger
  z-index: 11
.search-filter
  min-width: 280px
  z-index: 15 // higher than search results 10, higher than overlay 13, lower than mobile menu 20
  @screen lg
    z-index: 12 // higher than search results 10, lower than overlay 13
</style>

<script>
import WarehouseMain from '@/components/warehouses/search/area/main';
import WarehouseItem from '@/components/warehouses/search/area/warehouseItem';
import SearchFilter from '@/components/warehouses/search/area/searchFilter';
import Pagination from '@/components/warehouses/search/area/pagination';
import parseLinkHeader from 'parse-link-header';
import BreadcrumbsList from '@/components/breadcrumbsList.vue';
import Overlay from '@/components/warehouses/search/area/overlay';
import tagsMixin from '@/components/warehouses/search/area/tagsMixin';
import { isValid as isValidString } from '@/components/utils/string'
import Jsonld from '@/components/mixins/jsonld'

const SEARCH_TYPES = {
  FILTER: 'filter',
  TAG: 'tag'
}

const DEFAULT_PER_PAGE = 10

const convertSearchPropsToParams = (conditions, tags) => {
  const props = [];
  const searchTags = tags || [];
  props.push(`page=${parseInt(conditions.page) || 1}`);
  props.push(`page_size=${parseInt(conditions.perPage) || DEFAULT_PER_PAGE}`);
  props.push(`prefecture:${encodeURIComponent(conditions.prefecture)}`);
  if (conditions.area) {
    props.push(`area:${encodeURIComponent(conditions.area)}%E3%82%A8%E3%83%AA%E3%82%A2`);
  }

  // use tags passed or create tags from conditions
  if (!tags) {
    Object.keys(conditions).forEach((key) => {
      if (key === 'temperature' && conditions[key]) searchTags.push(`temperature_${conditions[key]}`);
      else if (key === 'structure' && conditions[key]) searchTags.push(`structure_${conditions[key]}`);
      else if (key === 'trucksize' && conditions[key]) searchTags.push(conditions[key]);
      else if ((conditions[key] == true) && !['page','perPage','prefecture','area'].includes(key)) searchTags.push(key);
    });
  }

  if (searchTags.length > 0) {
    console.log('Tags being searched... :', `[${searchTags.join(',')}]`);
    const encodedTags = encodeURIComponent(`[${searchTags.join(',')}]`);
    props.push(`tags:${encodedTags}`);
  }
  return `"${props.join(',')}"`;
}

const convertTagsToProps = (tags) => {
  const props = {};
  if (tags && tags.length > 0) {
    tags.forEach((tag) => {
      if (tag.startsWith('temperature')) props.temperature = tag.split('_')[1];
      else if (tag.startsWith('structure')) props.structure = tag.split('_')[1];
      else if (tag.startsWith('upto')) props.trucksize = tag;
      else props[tag] = true;
    });
  }
  return props;
}

const getQueryObjFromUrlParams = (query) => {
  // convert query from URL params
  return Object.keys(query).reduce((obj, key) => {
      obj[key] = (query[key] === 'true' ? true : query[key]);
      return obj;
    }, {}
  );
}

export default {
  components: { WarehouseMain, WarehouseItem, SearchFilter, Pagination, BreadcrumbsList, Overlay },
  mixins: [ tagsMixin, Jsonld ],
  meta: {
    feature: 'search'
  },
  async asyncData(context) {
    const { params, query, $warex } = context;

    // convert query from URL params
    const queryObj = getQueryObjFromUrlParams(query);

    const areaSearchProps = {
      prefecture: params.prefecture,
      area: params.area || null,
      ...queryObj
    };

    const { isError = false, headers, data: { warehouses = [], tags = [], total_warehouses_count } = {} } =
      await $warex.areaSearch(convertSearchPropsToParams(areaSearchProps));

    if (isError) return { isError };

    const { isError: isErrorAreas = false, data: publicAreas } = await $warex.getAreas();
    if (isErrorAreas) return { isError: isErrorAreas };

    // get paging information
    const { last, current } = parseLinkHeader(headers.link);

    const searchConditions = {
      prefecture: params.prefecture,
      area: params.area || null,
      ...queryObj,
      page: parseInt(current.page),
      perPage: parseInt(last.page_size) || DEFAULT_PER_PAGE
    };

    return {
      isError, params, isLoading: false,
      warehouses, publicAreas, tags,
      searchConditions, searchType: query.search_type, searchTag: query.tag,
      warehouseCount: total_warehouses_count,
      currentPage: parseInt(current.page), lastPage: parseInt(last.page)
    };
  },
  data () {
    return {
      isError: true,
      prefecture: this.$route.query.prefecture,
      filterDisplayed: false
    };
  },
  mounted () {
    if (this.isError) throw new Error('Server error occured'); // show 'error occured' image
    this.locationReady = true;
    this.isLoading = false;

    //save area for later after registration
    localStorage.setItem('prefecture', this.searchConditions.prefecture);
  },

  watch: {
    '$route.query'(next) {
      const queryObj = getQueryObjFromUrlParams(next);
      const params = convertSearchPropsToParams({
        prefecture: this.$route.params.prefecture,
        area: this.$route.params.area || null,
        ...queryObj
      });
      this.search(queryObj, params, queryObj.search_type, queryObj.tag, false);
    }
  },

  computed: {
    breadcrumbs () {
      const list = [{ url: '/warehouses/search/area', name: this.$t('search-warehouses') }];

      if (this.params.area) {
        list.push(
          { url: `/warehouses/search/area/${this.params.prefecture}`, name: this.params.prefecture },
          { url: this.$route.fullPath, name: this.$t('warehouses-at', { location: this.areaName }) },
        );
      } else {
        list.push({
          url: this.$route.fullPath,
          name: this.$t('warehouses-at', { location: this.params.prefecture }),
        });
      }

      return list;
    },
    isTagSearch () {
      return this.searchType === SEARCH_TYPES.TAG;
    },
    searchFilterDisplayed () {
      // pt-24 = header 60px + phone 35px
      return (this.filterDisplayed) ?
        'block fixed top-0 bottom-0 left-0 right-0 overflow-y-auto overflow-x-hidden pt-24' :
        'hidden';
    },
    global() {
      return this.$store.getters['cms/global']
    },
    homepage() {
      return this.$store.getters['cms/homepage']
    },
    areaLink() {
      const basePath = `/warehouses/search/area/${this.params.prefecture}/`;
      const areaData = this.publicAreas.find(item => item.prefecture === this.params.prefecture);
      if (!areaData) return [];

      return areaData.areas.map(area => ({
        url: `${basePath}${area}/`,
        name: this.$t('area-text', { area })
      }));
    },
    areaName() {
      return this.$t('area-text', { area: this.params.area });
    }
  },

  methods: {
    scrollToTop () {
      this.$nextTick(() => {
        window.scrollTo({ top: 0 });
      });
    },
    searchByFilter (newConditions) {
      this.saveConditionAndNavigate({ ...this.searchConditions, ...newConditions, page: 1 }, SEARCH_TYPES.FILTER);
      this.closeSearchFilter();
    },
    searchByTag (tag) {
      // reset condition
      this.searchConditions = { ...convertTagsToProps([tag]), prefecture: this.searchConditions.prefecture, page: 1 };

      const params = convertSearchPropsToParams({...this.searchConditions}, [ tag ]);
      this.search(this.searchConditions, params, SEARCH_TYPES.TAG, tag);
    },
    onPageChange (page) {
      this.saveConditionAndNavigate({ ...this.searchConditions, page });
    },
    onPageSizeChange (size) {
      this.saveConditionAndNavigate({ ...this.searchConditions, perPage: size, page: 1 });
    },

    async saveConditionAndNavigate (newConditions, searchType) {
      this.searchConditions = newConditions;
      const params = convertSearchPropsToParams(newConditions);
      this.search(newConditions, params, searchType);
    },

    async search (newConditions, searchParams, searchType, tag, updateUrlParam = true) {
      this.isLoading = true;
      const { isError = false, headers, data: { warehouses = [], tags = [], total_warehouses_count } = {} } =
        await this.$warex.areaSearch(searchParams);

      if (isError) throw new Error('server error occurred');

      // get paging information
      const { last, current } = parseLinkHeader(headers.link);

      this.warehouses = warehouses;
      this.tags = tags;
      this.searchType = searchType;
      this.searchTag = tag;
      this.warehouseCount = total_warehouses_count;
      this.currentPage = parseInt(current.page);
      this.lastPage = parseInt(last.page);

      if (updateUrlParam) {
        // update URL params (this won't update the content of the page)
        // build params object
        const query = Object.keys(newConditions).filter(key => newConditions[key])
          .reduce((obj, key) => {
            obj[key] = newConditions[key];
            return obj;
          }, {}
        );
        query.search_type = searchType;
        if (searchType === SEARCH_TYPES.TAG) query.tag = tag;

        const pathParams = [newConditions.prefecture];
        if (newConditions.area) {
          pathParams.push(newConditions.area);
        }

        this.$router.push(this.localeRoute({
          path: `/warehouses/search/area/${pathParams.join('/')}`,
          query: query
        }));

        //save area for later after registration
        localStorage.setItem('prefecture', pathParams.prefecture);
      }



      this.isLoading = false;

      this.scrollToTop();
    },
    showSearchFilter () {
      this.filterDisplayed = true;
    },
    closeSearchFilter () {
      this.filterDisplayed = false;
    }
  },

  head() {
    const { seo } = this.homepage;
    const { defaultSeo, siteName } = this.global;

    const location = this.params
      ? (this.params.area ? this.areaName : this.params.prefecture)
      : null;

    const pageText = this.currentPage > 1 ? `${this.currentPage}ページ目` : '';
    const extraTitle = pageText ? `（${pageText}）` : '';
    const extraDescription = pageText ? `の${pageText}` : '';

    const metaTitle = location ? `${location}の倉庫一覧${extraTitle}` : "全国の営業倉庫・貸し倉庫・物流倉庫一覧";
    const title = metaTitle;
    const metaDescription = `${location}の倉庫一覧（営業倉庫・貸し倉庫・物流倉庫）${extraDescription}です。気になる${location}の倉庫はすぐに料金確認を。新しい倉庫利用サービスWareXが、契約からご利用までサポート。`
    const sitename = isValidString(siteName) && siteName || ''
    const titleTemplate = `${title}${title && sitename ? '｜' : ''}${sitename}で料金を確認`;
    const metaKeywords = `${location},倉庫,ウェアエックス,WareX`;

    const fullSeo = {
      ...defaultSeo,
      ...seo,
      metaTitle,
      metaDescription,
      metaKeywords
    };

    if (this.isTagSearch || this.currentPage > 1 || this.searchConditions.perPage !== DEFAULT_PER_PAGE) {
      fullSeo.metaRobots = 'noindex';
    }

    return {
      ...(titleTemplate && { titleTemplate }),
      title,
      meta: this.$options.filters.cmsMetaTags(fullSeo),
      link: [
        { rel: 'canonical', hid: 'canonical', href: this.$seo.url(this.$route.fullPath) },
      ],
    }
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  }
}
</script>
